<template>
  <div class="my-licenses my-licenses--a">
    <loading v-if="loading"></loading>

    <div class="my-licenses__content">
      <!-- 顶部说明 -->
      <div class="my-licenses__header">
        <div class="my-licenses__header__title">软件许可</div>
        <div class="my-licenses__header__desc">
          这里能查到你提交的「许可申请」工单的发放状态，无需等待邮件
        </div>
      </div>

      <!-- 演示模式提示：仅 ?demo=1 时出现，正式客户看不到 -->
      <div v-if="isDemo" class="my-licenses__demo-flag">
        演示数据，仅用于预览页面效果
      </div>

      <!-- 列表 -->
      <div v-if="list.length > 0" class="my-licenses__list">
        <div v-for="item in list" :key="item.id" class="lic-card">
          <div class="lic-card__head">
            <div class="lic-card__head__software">{{ item.software }}</div>
            <div class="lic-card__head__status" :class="statusClass(item)">
              {{ statusText(item) }}
            </div>
          </div>

          <div class="lic-card__row">
            <span class="lic-card__row__label">机器码</span>
            <span class="lic-card__row__value lic-card__row__value--code">{{
              shortCode(item.machineCode)
            }}</span>
          </div>

          <div v-if="item.licenseStart" class="lic-card__row">
            <span class="lic-card__row__label">有效期</span>
            <span class="lic-card__row__value">
              {{ item.licenseStart }} 至 {{ item.licenseEnd }}
              <span v-if="expiringSoon(item)" class="lic-card__expiring">
                即将到期
              </span>
            </span>
          </div>

          <div v-if="item.email" class="lic-card__row">
            <span class="lic-card__row__label">接收邮箱</span>
            <span class="lic-card__row__value">{{ item.email }}</span>
          </div>

          <!-- 已发放：给下载链接和重发 -->
          <template v-if="item.status === 'ready'">
            <div class="lic-card__actions">
              <button
                class="lic-card__btn lic-card__btn--primary"
                type="button"
                @click="copyLink(item)"
              >
                复制下载链接
              </button>
              <button
                class="lic-card__btn"
                type="button"
                :disabled="resendingId === item.id"
                @click="resend(item)"
              >
                {{ resendingId === item.id ? "发送中…" : "重新发送到邮箱" }}
              </button>
            </div>
            <div class="lic-card__tip">
              许可是需要导入软件的文件，请把链接粘贴到<strong>电脑浏览器</strong>打开下载
            </div>
          </template>

          <!-- 待发放 -->
          <div v-else class="lic-card__pending">
            已收到申请，正在处理中。发放后可在本页查看，同时会发送到上方邮箱。
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading" class="my-licenses__empty">
        <img
          class="my-licenses__empty__img"
          src="@/assets/img/official-account/nolist.png"
          alt=""
        />
        <div class="my-licenses__empty__text">
          {{ loadError ? "暂时查询不到，请稍后重试" : "还没有许可申请记录" }}
        </div>
        <div class="my-licenses__empty__sub">
          提交「许可申请」类型的售后工单后，这里会显示发放状态
        </div>
        <div class="my-licenses__empty__btn" @click="goApply">去提交工单</div>
      </div>

      <!-- 刷新 -->
      <div v-if="!loading" class="my-licenses__refresh" @click="fetchList">
        点击刷新
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from "vant";
import { crmMyLicenses, crmResendLicense } from "@/api/crm";
import { initOpenid, initCodeApi } from "@/utils/getOpenid";
import { copyText } from "@/utils/clipboard";
import Loading from "./loading.vue";

// 演示数据：只在 URL 带 ?demo=1 时使用，用于在后端接口就绪前预览页面效果
const DEMO_LIST = [
  {
    id: 1,
    orderNo: "AS20260819001",
    software: "SoloLite",
    machineCode:
      "c9267cbe5e8a62a1f5cb349cd77f3c1aa6e790953d4328ae76cd5cc8baa461c2",
    status: "ready",
    downloadUrl: "https://crm.smartsolo.com/?Download=demo-token-example",
    licenseStart: "2026-08-19",
    licenseEnd: "2029-08-19",
    email: "619846860@qq.com",
  },
  {
    id: 2,
    orderNo: "AS20260819001",
    software: "DMC",
    machineCode:
      "16b95353c526a8daa98a5e0f16b95353c526a8daa98a5e0f16b95353c526a8da",
    status: "pending",
    downloadUrl: null,
    licenseStart: null,
    licenseEnd: null,
    email: "619846860@qq.com",
  },
];

export default {
  components: { Loading },
  data() {
    return {
      loading: true,
      loadError: false,
      list: [],
      resendingId: null,
    };
  },
  computed: {
    isDemo() {
      return this.$route.query.demo === "1";
    },
  },
  created() {
    if (this.isDemo) {
      this.list = DEMO_LIST;
      this.loading = false;
      return;
    }
    // openid 处理沿用项目里既有的写法（同 workOrderDetails.vue）
    const openid = localStorage.getItem("openId");
    if (!openid) {
      initOpenid(this.fetchList);
    } else if (openid === "null") {
      initCodeApi();
    } else {
      this.fetchList();
    }
  },
  methods: {
    async fetchList() {
      if (this.isDemo) return;
      this.loading = true;
      this.loadError = false;
      try {
        const { data, code } = await crmMyLicenses({
          openid: localStorage.getItem("openId"),
        });
        if (code !== "0000000") {
          this.loadError = true;
          this.list = [];
        } else {
          this.list = data || [];
        }
      } catch (e) {
        // 后端接口还没上线时会走到这里，静默降级为空状态，不弹错误吓到客户
        console.warn("查询许可失败", e);
        this.loadError = true;
        this.list = [];
      } finally {
        this.loading = false;
      }
    },

    statusText(item) {
      return item.status === "ready" ? "已发放" : "处理中";
    },
    statusClass(item) {
      return item.status === "ready"
        ? "lic-card__head__status--ready"
        : "lic-card__head__status--pending";
    },

    // 机器码有 64 位，全展开会把卡片撑得很长，中间省略即可，需要完整值时用复制
    shortCode(code) {
      if (!code) return "-";
      return code.length > 20
        ? code.slice(0, 10) + "…" + code.slice(-8)
        : code;
    },

    // 剩余 30 天内提示即将到期，避免客户等到软件打不开才发现
    expiringSoon(item) {
      if (!item.licenseEnd) return false;
      const end = new Date(String(item.licenseEnd).replace(/-/g, "/"));
      if (isNaN(end.getTime())) return false;
      const days = (end - new Date()) / 86400000;
      return days > 0 && days <= 30;
    },

    async copyLink(item) {
      if (!item.downloadUrl) return;
      const ok = await copyText(item.downloadUrl);
      if (ok) {
        Toast.success("链接已复制，请在电脑浏览器打开");
      } else {
        Toast.fail("复制失败，请长按链接手动复制");
      }
    },

    async resend(item) {
      if (this.resendingId) return;
      this.resendingId = item.id;
      try {
        const res = await crmResendLicense({
          openid: localStorage.getItem("openId"),
          id: item.id,
        });
        if (res.code === "0000000") {
          Toast.success(res.message || "已重新发送，请查收邮件");
        } else {
          Toast.fail(res.message || "发送失败，请稍后再试");
        }
      } catch (e) {
        Toast.fail("发送失败，请稍后再试");
      } finally {
        this.resendingId = null;
      }
    },

    goApply() {
      this.$router.push({ path: "/appraise" });
    },
  },
};
</script>

<style lang="less" scoped>
.my-licenses {
  background: #f5f5f5;
  min-height: 100vh;

  &__content {
    padding: 12px;
  }

  &__header {
    background: #fff;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 10px;

    &__title {
      font-size: 16px;
      font-weight: 700;
      color: #181818;
    }

    &__desc {
      margin-top: 4px;
      font-size: 12px;
      line-height: 1.6;
      color: #999;
    }
  }

  &__demo-flag {
    background: #fff7ed;
    border: 1px dashed rgba(245, 158, 11, 0.5);
    color: #b45309;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    text-align: center;
    margin-bottom: 10px;
  }

  &__empty {
    background: #fff;
    border-radius: 12px;
    padding: 32px 20px;
    text-align: center;

    &__img {
      width: 120px;
      height: auto;
      display: block;
      margin: 0 auto 12px;
    }

    &__text {
      font-size: 14px;
      color: #666;
    }

    &__sub {
      margin-top: 6px;
      font-size: 12px;
      line-height: 1.6;
      color: #bbb;
    }

    &__btn {
      display: inline-block;
      margin-top: 16px;
      padding: 8px 22px;
      border: 1px solid #60bb46;
      border-radius: 90px;
      color: #60bb46;
      font-size: 13px;

      &:active {
        background: #f0fae6;
      }
    }
  }

  &__refresh {
    margin: 16px auto 4px;
    text-align: center;
    font-size: 12px;
    color: #bbb;
    padding: 8px;
  }
}

.lic-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 10px;
    border-bottom: 1px solid #f2f2f2;

    &__software {
      font-size: 15px;
      font-weight: 600;
      color: #181818;
    }

    &__status {
      font-size: 12px;
      font-weight: 500;
      padding: 3px 10px;
      border-radius: 90px;

      &--ready {
        color: #60bb46;
        background: #f0fae6;
      }

      &--pending {
        color: #d97706;
        background: #fff7ed;
      }
    }
  }

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.5;

    &__label {
      flex-shrink: 0;
      width: 56px;
      color: #999;
    }

    &__value {
      flex: 1;
      min-width: 0;
      color: #333;
      word-break: break-all;

      &--code {
        font-family: Menlo, Consolas, monospace;
        letter-spacing: 0.2px;
      }
    }
  }

  &__expiring {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    color: #d97706;
    background: #fff7ed;
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
  }

  &__btn {
    flex: 1;
    height: 38px;
    border-radius: 90px;
    font-size: 13px;
    border: 1px solid #60bb46;
    background: #fff;
    color: #60bb46;
    transition: background 0.2s ease, opacity 0.2s ease;

    &:active {
      background: #f0fae6;
    }

    &:disabled {
      opacity: 0.55;
      pointer-events: none;
    }

    &--primary {
      background: #60bb46;
      color: #fff;

      &:active {
        background: #50a336;
      }
    }
  }

  &__tip {
    margin-top: 10px;
    padding: 8px 10px;
    background: #fff7ed;
    border-left: 3px solid #f59e0b;
    border-radius: 4px 8px 8px 4px;
    font-size: 12px;
    line-height: 1.6;
    color: #b45309;

    strong {
      font-weight: 600;
      color: #92400e;
    }
  }

  &__pending {
    margin-top: 12px;
    padding: 10px 12px;
    background: #f9f9f9;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1.6;
    color: #999;
  }
}
</style>

<!--
  改版方案 A（2026-09-22）：分组列表。只改样式和布局，文字不动。
  回退：去掉模板根节点上的 my-licenses--a。
-->
<style lang="less" scoped>
@a-bg: #f2f2f7;
@a-card: #fff;
@a-line: #e9e9ee;
@a-ink: #1c1c1e;
@a-sub: #8a8a8e;
@a-green: #60bb46;
@a-green-text: #3f9a2e;
@a-radius: 12px;

.my-licenses--a {
  background: @a-bg;

  .my-licenses__content {
    padding: 8px 16px calc(24px + env(safe-area-inset-bottom, 0px));
  }

  // 大标题
  .my-licenses__header {
    background: transparent;
    border-radius: 0;
    padding: 8px 4px 16px;
    margin: 0;

    &__title {
      font-size: 28px;
      font-weight: 700;
      line-height: 1.2;
      color: @a-ink;
    }
    &__desc {
      margin-top: 4px;
      font-size: 13px;
      color: @a-sub;
    }
  }

  .my-licenses__empty {
    border-radius: @a-radius;

    &__btn {
      border: none;
      border-radius: @a-radius;
      background: @a-bg;
      color: @a-green-text;
      font-size: 15px;
      padding: 10px 24px;
    }
  }

  .my-licenses__refresh {
    color: @a-green-text;
    font-size: 13px;
  }

  // 一条许可 = 一张分组卡片，每项一行
  .lic-card {
    padding: 0;
    margin-bottom: 18px;
    border-radius: @a-radius;
    overflow: hidden;

    &__head {
      padding: 13px 16px;
      border-bottom: none;

      &__software {
        font-size: 17px;
        font-weight: 600;
        color: @a-ink;
      }
      &__status {
        font-size: 12px;
        font-weight: 600;
        padding: 2px 10px;

        &--ready {
          color: #2e7d1f;
          background: #e4f5dd;
        }
        &--pending {
          color: #9a6100;
          background: #fff1d6;
        }
      }
    }

    &__row {
      position: relative;
      margin: 0;
      padding: 11px 16px;
      align-items: center;
      font-size: 15px;

      &::before {
        content: "";
        position: absolute;
        left: 16px;
        right: 0;
        top: 0;
        border-top: 1px solid @a-line;
      }
      &__label {
        width: auto;
        min-width: 64px;
        color: @a-sub;
      }
      &__value {
        text-align: right;
        color: @a-ink;

        &--code {
          font-size: 13px;
        }
      }
    }

    // 两个按钮改成两行可点的文字
    &__actions {
      flex-direction: column;
      gap: 0;
      margin: 0;
    }
    &__btn {
      position: relative;
      flex: none;
      height: 46px;
      padding: 0 16px;
      border: none;
      border-radius: 0;
      background: @a-card;
      color: @a-green-text;
      font-size: 15px;
      text-align: left;

      &::before {
        content: "";
        position: absolute;
        left: 16px;
        right: 0;
        top: 0;
        border-top: 1px solid @a-line;
      }
      &:active {
        background: #f2f2f5;
      }
      &--primary {
        background: @a-card;
        color: @a-green-text;
        font-weight: 600;

        &:active {
          background: #f2f2f5;
        }
      }
    }

    &__tip {
      margin: 0;
      padding: 10px 16px 12px;
      background: #fffaf0;
      border-left: none;
      border-radius: 0;
      border-top: 1px solid @a-line;
      color: #8f6a14;
    }

    &__pending {
      margin: 0;
      padding: 11px 16px 13px;
      background: transparent;
      border-radius: 0;
      border-top: 1px solid @a-line;
      color: @a-sub;
    }
  }
}
</style>
